import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { videoService, Video } from '../../services/videoService';
import { getAssetDownloadUrl, isVideoMediaUrl } from '../../services/assetUrlUtils';
import { canonicalVideoKey } from '../../components/canvas/utils/videoPreloader';
import { getVideoPoster } from '../../components/canvas/utils/videoPosterCache';
import {
  getFeatureFlags,
  cacheFeatureFlags,
  FEATURE_FLAGS_STORAGE_KEY,
} from '../../services/featureFlagsService';
import { useAuth } from '../../App';
import { ApiError } from '../../services/utils/apiError';
import BillingStatusCard from '../../components/billing/BillingStatusCard';
import ProfileSection from '../../components/profile/ProfileSection';
import SupportPage from '../../components/support/SupportPage';
import LogoIconSrc from '../../assets/icons/logo/pixle-logo.webp';
import './videoManagement.css';
import { toast } from 'react-toastify';
import { detectLanguage } from '../../i18n/language';
import { videoManagementMessage } from '../../i18n/videoManagementMessages';
import { getGaAnalyticsPayload } from '../../services/ga4Client';
import {
  Bell,
  CircleHelp,
  Clock3,
  Download,
  FolderKanban,
  Film,
  Gauge,
  LogOut,
  Menu,
  Plus,
  Share2,
  Sparkles,
  Star,
  TabletSmartphone,
  Trophy,
  TrendingUp,
  Trash2,
  Rocket,
  UserRound,
  WandSparkles,
  Video as VideoIcon,
  X,
  type LucideIcon,
} from 'lucide-react';

type DashboardSection =
  | 'overview'
  | 'myVideos'
  | 'sharedWithMe'
  | 'manageExports'
  | 'notifications'
  | 'support'
  | 'profile';

const overviewMetrics = [
  {
    id: 'totalProjects',
    title: 'Total Projects',
    value: '24',
    detail: '+3 this month',
    icon: FolderKanban,
  },
  {
    id: 'publishedEpisodes',
    title: 'Published Episodes',
    value: '138',
    detail: '+12 this week',
    icon: VideoIcon,
  },
  {
    id: 'characters',
    title: 'Characters',
    value: '17',
    detail: '4 active',
    icon: Sparkles,
  },
  {
    id: 'generatedAssets',
    title: 'Generated Assets',
    value: '2,481',
    detail: '+184 today',
    icon: WandSparkles,
  },
  {
    id: 'studioViews',
    title: 'Studio Views',
    value: '2.4M',
    detail: '+14%',
    icon: TrendingUp,
  },
  {
    id: 'aiTimeSaved',
    title: 'AI Time Saved',
    value: '312 hrs',
    detail: 'Compared to manual production',
    icon: Clock3,
  },
] as const;

const aiInsightItems = [
  {
    title: 'Character Opportunity',
    detail:
      'Alex has appeared in 62% of your highest-performing episodes.\nConsider pairing Alex with Mia in your next short.',
    icon: Sparkles,
    badge: 'High Confidence',
  },
  {
    title: 'Production Insight',
    detail:
      "Your production speed increased by 31% this month.\nYou're reusing approved character kits effectively.",
    icon: WandSparkles,
    badge: 'High Confidence',
  },
  {
    title: 'Growth Prediction',
    detail:
      'Episodes under 60 seconds are projected to perform best.\nEstimated audience lift: +18%',
    icon: TrendingUp,
    badge: 'Moderate Confidence',
  },
] as const;

const recentWinItems = [
  {
    title: 'Brand Launch Cut published successfully',
    meta: 'Reached 120k views in the demo workspace',
    status: 'Published',
    icon: Trophy,
  },
  {
    title: 'Alex is now your highest-performing character',
    meta: 'Average completion rate: 84%',
    status: 'Trending',
    icon: Star,
  },
  {
    title: 'Weekly Story Reel exported in 2m 48s',
    meta: '42% faster than your average production time',
    status: 'Improved',
    icon: Rocket,
  },
] as const;

const getStoredFirstName = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return storedUser.firstName || 'Guest';
  } catch {
    return 'Guest';
  }
};

/**
 * Renders a muted, non-playing video thumbnail. When a first-frame poster was
 * captured during a previous editor visit, it paints instantly with zero
 * network (shared canonical-key cache, immune to SAS refreshes) and the video
 * element only mounts on hover. Otherwise it paints the video's first frame
 * as a static preview (`preload="metadata"`) and only plays while hovered,
 * resetting back to the first frame on mouse leave.
 */
const VideoPreview = ({ src }: { src: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playable, setPlayable] = useState(false);
  const poster = getVideoPoster(canonicalVideoKey(src));

  useEffect(() => {
    setPlayable(false);
  }, [src]);

  const handleMouseEnter = () => {
    if (poster && !playable) {
      setPlayable(true);
      return;
    }
    videoRef.current?.play().catch(() => {});
  };

  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  if (poster && !playable) {
    return <img src={poster} alt="Video preview" onMouseEnter={handleMouseEnter} />;
  }

  return (
    <video
      ref={videoRef}
      src={src}
      muted
      loop
      playsInline
      preload="metadata"
      autoPlay={playable}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onError={(e) => (e.currentTarget.style.display = 'none')}
    />
  );
};

const VideoManagement: React.FC = () => {
  const [myVideos, setMyVideos] = useState<Video[]>([]);
  const [sharedVideos, setSharedVideos] = useState<Video[]>([]);
  const [insightsV2Enabled, setInsightsV2Enabled] = useState(false);
  const [deleteVideoEnabled, setDeleteVideoEnabled] = useState(false);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  // @ts-ignore
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] =
    useState<DashboardSection>('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useAuth();
  const sessionExpiredHandledRef = useRef(false);
  // Greeting name, re-read from storage whenever the profile is saved.
  const [firstName, setFirstName] = useState(getStoredFirstName);

  const handleSectionChange = (section: DashboardSection) => {
    setActiveSection(section);
    setMobileNavOpen(false);
    navigate(section === 'overview' ? '/' : `/?section=${section}`);
  };

  // Close the mobile drawer on Escape and lock body scroll while open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section') as DashboardSection | null;

    if (
      section === 'overview' ||
      section === 'myVideos' ||
      section === 'sharedWithMe' ||
      section === 'manageExports' ||
      section === 'notifications' ||
      section === 'support' ||
      section === 'profile'
    ) {
      setActiveSection(section);
      return;
    }

    if (location.pathname === '/') {
      setActiveSection('overview');
    }
  }, [location.pathname, location.search]);

  const navItems: Array<{
    id: DashboardSection;
    label: string;
    icon: LucideIcon;
    count?: number;
  }> = [
    { id: 'overview', label: 'Overview', icon: Gauge },
    { id: 'myVideos', label: 'My Videos', icon: Film, count: myVideos.length },
    {
      id: 'sharedWithMe',
      label: 'Shared with me',
      icon: Share2,
      count: sharedVideos.length,
    },
    { id: 'manageExports', label: 'Manage exports', icon: Download },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'support', label: 'Support', icon: CircleHelp },
  ];

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // Ensures AM/PM format
    });
  };

  const formatTileDate = (isoString: string) => {
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getFirstSceneBackgroundImage = (video: Video): string | undefined => {
    const firstSceneBackground = video.scenes?.[0]?.backgroundImage;

    if (firstSceneBackground) {
      return firstSceneBackground;
    }

    const backgroundRow = video.timeline?.find?.(
      (row: { itemId?: string }) => row.itemId === 'background-row'
    );
    const firstBackgroundAction = backgroundRow?.actions?.[0];

    // Prefer the real video asset (videoData) over the duplicated imageData
    // field when a generated video has been attached to the scene.
    return (
      firstBackgroundAction?.data?.videoData ||
      firstBackgroundAction?.data?.imageData ||
      undefined
    );
  };

  const handleProjectClick = (id: string, title: string) => {
    navigate(`/video/${id}`, { state: { title } }); // Pass title in state
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    setIsAuthenticated(false);
  }, [setIsAuthenticated]);

  const handleLogout = () => {
    clearSession();
    toast.success(videoManagementMessage('logoutSuccess'));
    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2000);
  };

  /**
   * Forced logout when the home page's initial data calls fail with an
   * auth error (401 expired/invalid token, 403 revoked/forbidden access).
   * Network and server errors (5xx) are excluded so a transient outage
   * doesn't force the user to log back in. Ensures we only redirect once
   * even when multiple parallel requests fail at the same time.
   */
  const isSessionError = useCallback((err: unknown): boolean => {
    return err instanceof ApiError && (err.status === 401 || err.status === 403);
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredHandledRef.current) return;
    sessionExpiredHandledRef.current = true;
    clearSession();
    toast.error(videoManagementMessage('sessionExpired'));
    navigate('/login', { replace: true });
  }, [clearSession, navigate]);

  const handleCreateNew = async () => {
    try {
      const analytics = await getGaAnalyticsPayload(true);
      const newVideo = await videoService.createVideo(
        {
          title: 'Untitled',
          description: '',
          thumbnailUrl: '',
          duration: 0,
        },
        analytics
      );
      navigate(`/video/${newVideo._id}`);
    } catch (error) {
      console.error('Error creating video:', error);
      setError('Failed to create video');
    }
  };

  const handleDeleteVideo = async (video: Video) => {
    const language = detectLanguage();
    if (
      !window.confirm(
        videoManagementMessage(
          'deleteVideoConfirm',
          { title: video.title },
          language
        )
      )
    ) {
      return;
    }

    setDeletingVideoId(video._id);
    try {
      await videoService.deleteVideo(video._id);
      setMyVideos((prev) => prev.filter((item) => item._id !== video._id));
      toast.success(videoManagementMessage('videoDeleted', {}, language));
    } catch (err) {
      console.error('Failed to delete video', err);
      if (isSessionError(err)) {
        handleSessionExpired();
      } else {
        toast.error(videoManagementMessage('videoDeleteFailed', {}, language));
      }
    } finally {
      setDeletingVideoId(null);
    }
  };

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const featureFlags = await getFeatureFlags();
        cacheFeatureFlags(featureFlags);
        setInsightsV2Enabled(Boolean(featureFlags.insights_v2));
        setDeleteVideoEnabled(featureFlags.delete_video_enabled === true);
      } catch (err) {
        console.error('Failed to load feature flags', err);
        if (isSessionError(err)) {
          handleSessionExpired();
        } else {
          setInsightsV2Enabled(false);
          setDeleteVideoEnabled(false);
        }
      }
    };

    const fetchMyVideos = async () => {
      try {
        const videos = await videoService.getMyVideos();
        setMyVideos(videos);
      } catch (err) {
        console.error('Failed to load my videos', err);
        if (isSessionError(err)) {
          handleSessionExpired();
        } else {
          setError('Failed to load your videos');
        }
      }
    };

    const fetchSharedVideos = async () => {
      try {
        const videos = await videoService.getSharedVideos();
        setSharedVideos(videos);
      } catch (err) {
        console.error('Failed to load shared videos', err);
        if (isSessionError(err)) {
          handleSessionExpired();
        } else {
          setError('Failed to load shared videos');
        }
      }
    };

    fetchFeatureFlags();
    fetchMyVideos();
    fetchSharedVideos();
  }, [clearSession, handleSessionExpired, isSessionError]);

  const renderVideoTile = (video: Video, canDelete = false) => {
    const firstSceneBackgroundImage = getFirstSceneBackgroundImage(video);
    // The document stores a relative blob path; resolve it into a playable URL
    // using the SAS token returned alongside the video.
    const resolvedBackground = getAssetDownloadUrl(
      firstSceneBackgroundImage,
      video.sas?.token
    );
    const isVideoBackground = isVideoMediaUrl(resolvedBackground);

    return (
      <article className="video-tile-shell" key={video._id}>
        <button
          type="button"
          className="video-tile"
          onClick={() => handleProjectClick(video._id, video.title)}
        >
          <div className="video-preview">
            {isVideoBackground && resolvedBackground ? (
              <VideoPreview src={resolvedBackground} />
            ) : resolvedBackground ? (
              <img
                src={resolvedBackground}
                alt={`${video.title} first scene`}
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
            ) : (
              <div className="video-preview-placeholder">
                <VideoIcon size={28} strokeWidth={1.8} />
              </div>
            )}
          </div>

          <p className="video-card-title">{video.title}</p>
          <span
            className="date"
            title={`Last updated at ${formatDate(video.updatedAt)}`}
          >
            Updated {formatTileDate(video.updatedAt)}
          </span>
        </button>

        {canDelete && (
          <button
            type="button"
            className="video-tile-delete"
            aria-label={`Delete ${video.title}`}
            title="Delete video"
            disabled={deletingVideoId === video._id}
            onClick={(event) => {
              event.stopPropagation();
              void handleDeleteVideo(video);
            }}
          >
            <Trash2 size={16} strokeWidth={2.2} />
          </button>
        )}
      </article>
    );
  };

  const renderEmptyState = (message: string) => {
    return (
      <div className="empty-state">
        <div className="empty-icon">
          <Sparkles size={22} strokeWidth={1.9} />
        </div>
        <p>{message}</p>
        <button
          type="button"
          className="secondary-action"
          onClick={handleCreateNew}
        >
          <Plus size={16} strokeWidth={2.2} />
          Create video
        </button>
      </div>
    );
  };

  const renderMetricCard = ({
    id,
    title,
    value,
    detail,
    icon: Icon,
  }: (typeof overviewMetrics)[number]) => {
    return (
      <article className="metric-card" key={id}>
        <div className="metric-card-icon">
          <Icon size={17} strokeWidth={2} />
        </div>
        <p className="metric-card-title">{title}</p>
        <strong className="metric-card-value">{value}</strong>
        <span className="metric-card-detail">{detail}</span>
      </article>
    );
  };

  const renderLegacyOverview = () => (
    <>
      <header className="dashboard-header">
        <div>
          <p className="eyebrow">Pixle Studio</p>
          <h1>Welcome back, {firstName}</h1>
        </div>
      </header>

      <section
        className="quick-create-band"
        aria-labelledby="start-creating"
      >
        <div className="quick-create-copy">
          <span className="section-kicker">
            <Sparkles size={18} strokeWidth={1.9} />
            Start creating
          </span>
          <h2 id="start-creating">Create from scratch</h2>

          <div className="overview-stats" aria-label="Video overview">
            <button
              type="button"
              className="stat-pill stat-pill-purple stat-pill-link"
              onClick={() => handleSectionChange('myVideos')}
              aria-label={`Open my videos. ${myVideos.length} videos available.`}
            >
              <strong>{myVideos.length}</strong>
              <span>My videos</span>
            </button>
            <button
              type="button"
              className="stat-pill stat-pill-cyan stat-pill-link"
              onClick={() => handleSectionChange('sharedWithMe')}
              aria-label={`Open shared with me. ${sharedVideos.length} videos available.`}
            >
              <strong>{sharedVideos.length}</strong>
              <span>Shared</span>
            </button>
          </div>
        </div>

        <button
          type="button"
          className="create-card"
          onClick={handleCreateNew}
        >
          <span className="create-icon-box">
            <Plus size={30} strokeWidth={2.2} />
          </span>
          <span>
            <strong>New video</strong>
            <small>Start a new Pixle project</small>
          </span>
        </button>
      </section>

      {activeSection === 'myVideos' && (
        <section className="dashboard-section my-videos">
          <div className="section-header">
            <div>
              <h2>My Videos</h2>
              <p className="section-subtitle">{myVideos.length} projects</p>
            </div>
          </div>
          <div className="projects-container">
            {myVideos.length > 0
              ? myVideos.map((video) =>
                  renderVideoTile(video, deleteVideoEnabled)
                )
              : renderEmptyState('No videos yet.')}
          </div>
        </section>
      )}
      {activeSection === 'sharedWithMe' && (
        <section className="dashboard-section shared-with-me">
          <div className="section-header">
            <div>
              <h2>Shared with Me</h2>
              <p className="section-subtitle">
                {sharedVideos.length} projects
              </p>
            </div>
          </div>
          <div className="projects-container">
            {sharedVideos.length > 0
              ? sharedVideos.map((video) => renderVideoTile(video))
              : renderEmptyState('No shared videos yet.')}
          </div>
        </section>
      )}
      {activeSection === 'manageExports' && (
        <section className="dashboard-section manage-exports">
          <div className="section-header">
            <div>
              <h2>Manage Exports</h2>
              <p className="section-subtitle">Illustrative export workspace</p>
            </div>
          </div>
          <div className="exports-page-grid">
            <div className="status-panel exports-summary-panel">
              <Download size={24} strokeWidth={1.9} />
              <p>Export queue and delivery controls live here.</p>
            </div>
            <div className="exports-history-card">
              <div className="exports-history-header">
                <h3>Recent wins</h3>
                <span>3 items</span>
              </div>
              <div className="recent-win-list">
                {recentWinItems.map((item) => (
                  <article className="recent-win-card" key={item.title}>
                    <div className="recent-win-card-header">
                      <div className="recent-win-icon">
                        <item.icon size={17} strokeWidth={2} />
                      </div>
                      <div className="recent-win-copy">
                        <strong>{item.title}</strong>
                        <p>{item.meta}</p>
                      </div>
                    </div>
                    <span className={`export-status ${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      {activeSection === 'notifications' && (
        <section className="dashboard-section notifications">
          <div className="section-header">
            <div>
              <h2>Notifications</h2>
              <p className="section-subtitle">All caught up</p>
            </div>
          </div>
          <div className="status-panel">
            <Bell size={24} strokeWidth={1.9} />
            <p>No notifications right now.</p>
          </div>
        </section>
      )}
      {activeSection === 'support' && <SupportPage />}
    </>
  );

  const renderLegacyManageExports = () => (
    <section className="dashboard-section manage-exports">
      <div className="section-header">
        <div>
          <h2>Manage Exports</h2>
          <p className="section-subtitle">0 exports</p>
        </div>
      </div>
      <div className="status-panel exports-summary-panel">
        <Download size={24} strokeWidth={1.9} />
        <p>No exports yet.</p>
      </div>
    </section>
  );

  const renderInsightsManageExports = () => (
    <section className="dashboard-section manage-exports">
      <div className="section-header">
        <div>
          <h2>Manage Exports</h2>
          <p className="section-subtitle">Illustrative export workspace</p>
        </div>
      </div>
      <div className="exports-page-grid">
        <div className="status-panel exports-summary-panel">
          <Download size={24} strokeWidth={1.9} />
          <p>Export queue and delivery controls live here.</p>
        </div>
        <div className="exports-history-card">
          <div className="exports-history-header">
            <h3>Recent wins</h3>
            <span>3 items</span>
          </div>
          <div className="recent-win-list">
            {recentWinItems.map((item) => (
              <article className="recent-win-card" key={item.title}>
                <div className="recent-win-card-header">
                  <div className="recent-win-icon">
                    <item.icon size={17} strokeWidth={2} />
                  </div>
                  <div className="recent-win-copy">
                    <strong>{item.title}</strong>
                    <p>{item.meta}</p>
                  </div>
                </div>
                <span className={`export-status ${item.status.toLowerCase()}`}>
                  {item.status}
                </span>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  const renderInsightsOverview = () => (
    <>
      <section
        className="studio-overview"
        aria-labelledby="studio-overview-title"
      >
        <div className="studio-overview-hero">
          <div className="studio-overview-copy">
            <span className="demo-badge">
              <Star size={12} strokeWidth={2.3} />
              Demo Workspace
            </span>
            <p className="eyebrow">Pixle Studio</p>
            <h1 id="studio-overview-title">Studio Overview</h1>
            <p className="hero-description">
              Your AI production studio. Create, publish and grow animated
              series with AI-powered creative insights.
            </p>
          </div>

          <div className="hero-action-card">
            <div className="hero-action-header">
              <div className="hero-action-icon">
                <Sparkles size={18} strokeWidth={2} />
              </div>
              <div>
                <p>AI Producer</p>
                <span>Creative guidance from your workspace</span>
              </div>
            </div>
            <button
              type="button"
              className="primary-action"
              onClick={handleCreateNew}
            >
              <Plus size={18} strokeWidth={2.3} />
              New video
            </button>
          </div>
        </div>

        <div className="metric-grid" aria-label="Studio metrics">
          {overviewMetrics.map(renderMetricCard)}
        </div>
      </section>

      <section className="dashboard-insights-grid">
        <article className="insights-panel">
          <div className="section-header compact">
            <div>
              <p className="section-kicker">
                <WandSparkles size={16} strokeWidth={1.9} />
                AI Producer
              </p>
              <h2>Recommendations generated from your creative workspace</h2>
            </div>
            <span className="panel-chip">Illustrative</span>
          </div>

          <div className="recommendation-list">
            {aiInsightItems.map((insight) => (
              <article className="recommendation-card" key={insight.title}>
                <div className="recommendation-card-header">
                  <div className="recommendation-icon">
                    <insight.icon size={17} strokeWidth={2} />
                  </div>
                  <div className="recommendation-copy">
                    <strong>{insight.title}</strong>
                    <p>{insight.detail}</p>
                  </div>
                </div>
                <span className="confidence-badge">{insight.badge}</span>
              </article>
            ))}
          </div>
        </article>

        <article className="recent-exports-panel">
          <div className="section-header compact">
            <div>
              <p className="section-kicker">
                <TabletSmartphone size={16} strokeWidth={1.9} />
                Recent Wins
              </p>
              <h2>Creator progress worth celebrating</h2>
            </div>
            <button
              type="button"
              className="text-link-button"
              onClick={() => setActiveSection('manageExports')}
            >
              Open exports
            </button>
          </div>

          <div className="recent-win-list">
            {recentWinItems.map((item) => (
              <article className="recent-win-card" key={item.title}>
                <div className="recent-win-card-header">
                  <div className="recent-win-icon">
                    <item.icon size={17} strokeWidth={2} />
                  </div>
                  <div className="recent-win-copy">
                    <strong>{item.title}</strong>
                    <p>{item.meta}</p>
                  </div>
                </div>
                <span className={`export-status ${item.status.toLowerCase()}`}>
                  {item.status}
                </span>
              </article>
            ))}
          </div>
        </article>
      </section>
    </>
  );

  return (
    <div className="video-management-page">
      <header className="mobile-topbar">
        <button
          type="button"
          className="mobile-menu-button"
          onClick={() => setMobileNavOpen((open) => !open)}
          aria-expanded={mobileNavOpen}
          aria-controls="dashboard-sidebar"
          aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileNavOpen ? (
            <X size={22} strokeWidth={2.2} />
          ) : (
            <Menu size={22} strokeWidth={2.2} />
          )}
        </button>
        <img
          src={LogoIconSrc}
          className="mobile-topbar-logo"
          alt="Pixle Studio"
          width="120"
          height="36"
        />
        <button
          type="button"
          className="mobile-create-button"
          onClick={handleCreateNew}
          aria-label="Create new video"
        >
          <Plus size={20} strokeWidth={2.4} />
        </button>
      </header>

      <div
        className={`sidebar-backdrop ${mobileNavOpen ? 'visible' : ''}`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden={!mobileNavOpen}
      />
      <aside
        id="dashboard-sidebar"
        className={`dashboard-sidebar ${mobileNavOpen ? 'open' : ''}`}
      >
        <div className="brand-lockup">
          <img
            src={LogoIconSrc}
            className="dashboard-logo"
            alt="Pixle Studio"
            width="150"
            height="44"
          />
        </div>

        <nav className="sidebar-nav" aria-label="Dashboard sections">
          {navItems.map(({ id, label, icon: Icon, count }) => (
            <button
              type="button"
              key={id}
              className={`nav-link ${activeSection === id ? 'active' : ''}`}
              onClick={() => handleSectionChange(id)}
            >
              <span className="nav-icon">
                <Icon size={18} strokeWidth={2} />
              </span>
              <span>{label}</span>
              {typeof count === 'number' && (
                <span className="nav-count">{count}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="user-section">
          <button
            type="button"
            className="user-details user-details-link"
            onClick={() => handleSectionChange('profile')}
            title="Open profile settings"
            aria-label="Open profile settings"
          >
            <div className="user-avatar">
              <UserRound size={18} strokeWidth={2.1} />
            </div>
            <span>Welcome back {firstName}</span>
          </button>

          <button className="logout-button" onClick={handleLogout}>
            <LogOut size={16} strokeWidth={2.1} />
            Logout
          </button>
        </div>
      </aside>

      <main className="dashboard-content">
        {activeSection === 'overview' && (
          <>
            <BillingStatusCard />
            {insightsV2Enabled
              ? renderInsightsOverview()
              : renderLegacyOverview()}
          </>
        )}

        {activeSection === 'myVideos' && (
          <section className="dashboard-section my-videos">
            <div className="section-header">
              <div>
                <h2>My Videos</h2>
                <p className="section-subtitle">
                  {myVideos.length} projects
                </p>
              </div>
              <button
                type="button"
                className="primary-action"
                onClick={handleCreateNew}
              >
                <Plus size={18} strokeWidth={2.3} />
                New video
              </button>
            </div>
            <div className="projects-container">
              {myVideos.length > 0
                ? myVideos.map((video) =>
                    renderVideoTile(video, deleteVideoEnabled)
                  )
                : renderEmptyState('No videos yet.')}
            </div>
          </section>
        )}
        {activeSection === 'sharedWithMe' && (
          <section className="dashboard-section shared-with-me">
            <div className="section-header">
              <div>
                <h2>Shared with Me</h2>
                <p className="section-subtitle">
                  {sharedVideos.length} projects
                </p>
              </div>
              <button
                type="button"
                className="primary-action"
                onClick={handleCreateNew}
              >
                <Plus size={18} strokeWidth={2.3} />
                New video
              </button>
            </div>
            <div className="projects-container">
              {sharedVideos.length > 0
                ? sharedVideos.map((video) => renderVideoTile(video))
                : renderEmptyState('No shared videos yet.')}
            </div>
          </section>
        )}
      {activeSection === 'manageExports' &&
        (insightsV2Enabled
          ? renderInsightsManageExports()
          : renderLegacyManageExports())}
        {activeSection === 'notifications' && (
          <section className="dashboard-section notifications">
            <div className="section-header">
              <div>
                <h2>Notifications</h2>
                <p className="section-subtitle">All caught up</p>
              </div>
            </div>
            <div className="status-panel">
              <Bell size={24} strokeWidth={1.9} />
              <p>No notifications right now.</p>
            </div>
          </section>
        )}
        {activeSection === 'support' && <SupportPage />}
        {activeSection === 'profile' && (
          <ProfileSection onProfileSaved={() => setFirstName(getStoredFirstName())} />
        )}
      </main>
    </div>
  );
};

export default VideoManagement;
